import { DtoBase } from "../Utils/DtoBase";
import { ValueEncoder } from "../Utils/ValueEncoder";
import { List } from "../Utils/List";
import { TimeSpan } from "../Utils/TimeSpan";
import { ObjUtils } from "../Utils/ObjUtils";
import { IpScope } from "../Core/IpScope";
import { ProtocolSettingsSet } from "../Protocols/ProtocolSettingsSet";

export enum DiscoveryMethod { None, PingSweep, Crawl, NeighborCrawl };


export class DiscoverySettings extends DtoBase {

	public static readonly DefaultName = "";
	public static readonly DefaultDescription = "";
	public static readonly DefaultMaxThreads = 40;
	public static readonly DefaultPingFirst = true;
	//public static readonly DefaultPingFirst = false;
	public static readonly DefaultPingTimeout = 2000;
	public static readonly DefaultPingRetries = 1;
	public static readonly DefaultResolveHostnames = true;
	public static readonly DefaultResolveNetBIOS = true;
	public static readonly DefaultCaptureConfigs = false;
	public static readonly DefaultCaptureLogs = false;
	public static readonly DefaultEnableCliDiscovery = false;
	public static readonly DefaultAllowIPv6Crawl = false;
	public static readonly DefaultTcpPorts = "";
	public static readonly DefaultUdpPorts = "";
	public static readonly DefaultKeepOpenPortsOnly = false;

	public Name: string;
	public Description: string;
	public Method: DiscoveryMethod;
	public MaxThreads: number;
	public PingFirst: boolean;
	public PingTimeout: number;
	public PingRetries: number;
	public ResolveHostnames: boolean;
	public ResolveNetBIOS: boolean;
	public EnableCliDiscovery: boolean;
	public AllowIPv6Crawl: boolean;
	public CaptureConfigs: boolean;
	public CaptureLogs: boolean;
	public SeedScope: IpScope;
	public IncludeScope: IpScope;
	public IncludeCategories: List<string>;
	public get HasIncludeCategories(): boolean {
		return (this.IncludeCategories && this.IncludeCategories.Count > 0);
	}
	public ExcludeScope: IpScope;
	public ExcludeCategories: List<string>;
	public get HasExcludeCategories(): boolean {
		return (this.ExcludeCategories && this.ExcludeCategories.Count > 0);
	}
	public ProtocolSettings: ProtocolSettingsSet;
	public MaxTotalTime: TimeSpan;
	public MaxDeviceTime: TimeSpan;
	public SnmpReadDelay: number;
	public WinFullInventory: boolean;
	public TcpPorts: string;
	public UdpPorts: string;
	public KeepOpenPortsOnly: boolean;

	constructor() {
		super();

		this.Name = DiscoverySettings.DefaultName;
		this.Description = DiscoverySettings.DefaultDescription;
		this.Method = DiscoveryMethod.None;
		this.MaxThreads = DiscoverySettings.DefaultMaxThreads;
		this.PingFirst = DiscoverySettings.DefaultPingFirst;
		this.PingTimeout = DiscoverySettings.DefaultPingTimeout;
		this.PingRetries = DiscoverySettings.DefaultPingRetries;
		this.ResolveHostnames = DiscoverySettings.DefaultResolveHostnames;
		this.ResolveNetBIOS = DiscoverySettings.DefaultResolveNetBIOS;
		this.CaptureConfigs = DiscoverySettings.DefaultCaptureConfigs;
		this.CaptureLogs = DiscoverySettings.DefaultCaptureLogs;
		this.EnableCliDiscovery = DiscoverySettings.DefaultEnableCliDiscovery;
		this.AllowIPv6Crawl = DiscoverySettings.DefaultAllowIPv6Crawl;
		this.SeedScope = new IpScope();
		this.IncludeScope = new IpScope();
		this.IncludeCategories = new List<string>();
		this.ExcludeScope = new IpScope();
		this.ExcludeCategories = new List<string>();
		this.ProtocolSettings = new ProtocolSettingsSet();
		this.MaxTotalTime = new TimeSpan();
		this.MaxDeviceTime = new TimeSpan();
		this.SnmpReadDelay = 0;
		this.WinFullInventory = false;
		this.TcpPorts = DiscoverySettings.DefaultTcpPorts;
		this.UdpPorts = DiscoverySettings.DefaultUdpPorts;
		this.KeepOpenPortsOnly = DiscoverySettings.DefaultKeepOpenPortsOnly;
	}

	public Clear(): void {
		this.Name = DiscoverySettings.DefaultName;
		this.Description = DiscoverySettings.DefaultDescription;
		this.Method = DiscoveryMethod.None;
		this.MaxThreads = DiscoverySettings.DefaultMaxThreads;
		this.PingFirst = DiscoverySettings.DefaultPingFirst;
		this.PingTimeout = DiscoverySettings.DefaultPingTimeout;
		this.PingRetries = DiscoverySettings.DefaultPingRetries;
		this.ResolveHostnames = DiscoverySettings.DefaultResolveHostnames;
		this.ResolveNetBIOS = DiscoverySettings.DefaultResolveNetBIOS;
		this.CaptureConfigs = DiscoverySettings.DefaultCaptureConfigs;
		this.CaptureLogs = DiscoverySettings.DefaultCaptureLogs;
		this.EnableCliDiscovery = DiscoverySettings.DefaultEnableCliDiscovery;
		this.AllowIPv6Crawl = DiscoverySettings.DefaultAllowIPv6Crawl;
		this.SeedScope.Clear();
		this.IncludeScope.Clear();
		this.IncludeCategories.Clear();
		this.ExcludeScope.Clear();
		this.ExcludeCategories.Clear();
		this.ProtocolSettings.Clear();
		this.MaxTotalTime = new TimeSpan();
		this.MaxDeviceTime = new TimeSpan();
		this.SnmpReadDelay = 0;
		this.WinFullInventory = false;
		this.TcpPorts = DiscoverySettings.DefaultTcpPorts;
		this.UdpPorts = DiscoverySettings.DefaultUdpPorts;
		this.KeepOpenPortsOnly = DiscoverySettings.DefaultKeepOpenPortsOnly;
	}

	public Clone(): DiscoverySettings {
		let obj = {};
		let context = {};
		this.Write(obj, context);

		let clonedSettings = new DiscoverySettings();
		clonedSettings.Read(obj, context);

		return clonedSettings;
	}

	public get HasName(): boolean {
		return !!this.Name;
	}

	public get HasDescription(): boolean {
		return !!this.Description;
	}

	public SetMaxTotalTime(minutes: number): void {
		if (minutes <= 0)
			this.ClearMaxTotalTime();
		else
			this.MaxTotalTime = new TimeSpan(0, minutes, 0);
	}

	public ClearMaxTotalTime(): void {
		this.MaxTotalTime = new TimeSpan();
	}

	public get HasMaxTotalTime(): boolean {
		return !this.MaxTotalTime.IsEmpty;
	}

	public ClearMaxDeviceTime(): void {
		this.MaxDeviceTime = new TimeSpan();
	}

	public get HasMaxDeviceTime(): boolean {
		return !this.MaxDeviceTime.IsEmpty;
	}

	public SetMaxDeviceTime(minutes: number): void {
		if (minutes <= 0)
			this.ClearMaxDeviceTime();
		else
			this.MaxDeviceTime = new TimeSpan(0, minutes, 0);
	}

	public get HasSnmpReadDelay(): boolean {
		return this.SnmpReadDelay > 0;
	}

	public get HasTcpPorts(): boolean {
		return !!this.TcpPorts;
	}
	public get HasUdpPorts(): boolean {
		return !!this.UdpPorts;
	}

	public Equals(obj: any): boolean {
		if (!super.Equals(obj))
			return false;

		if (!(obj instanceof DiscoverySettings))
			return false;
		let other = (obj as DiscoverySettings);

		return (ObjUtils.Equals(this.Name, other.Name) &&
			ObjUtils.Equals(this.Description, other.Description) &&
			this.Method === other.Method &&
			this.MaxThreads === other.MaxThreads &&
			this.PingFirst === other.PingFirst &&
			this.PingTimeout === other.PingTimeout &&
			this.PingRetries === other.PingRetries &&
			this.ResolveHostnames === other.ResolveHostnames &&
			this.ResolveNetBIOS === other.ResolveNetBIOS &&
			this.CaptureConfigs === other.CaptureConfigs &&
			this.CaptureLogs === other.CaptureLogs &&
			this.EnableCliDiscovery === other.EnableCliDiscovery &&
			this.AllowIPv6Crawl === other.AllowIPv6Crawl &&
			ObjUtils.Equals(this.SeedScope, other.SeedScope) &&
			ObjUtils.Equals(this.IncludeScope, other.IncludeScope) &&
			ObjUtils.ListEquals(this.IncludeCategories, other.IncludeCategories) &&
			ObjUtils.Equals(this.ExcludeScope, other.ExcludeScope) &&
			ObjUtils.ListEquals(this.ExcludeCategories, other.ExcludeCategories) &&
			ObjUtils.Equals(this.ProtocolSettings, other.ProtocolSettings) &&
			ObjUtils.Equals(this.MaxTotalTime, other.MaxTotalTime) &&
			ObjUtils.Equals(this.MaxDeviceTime, other.MaxDeviceTime) &&
			this.SnmpReadDelay === other.SnmpReadDelay &&
			this.WinFullInventory === other.WinFullInventory &&
			ObjUtils.Equals(this.TcpPorts, other.TcpPorts) &&
			ObjUtils.Equals(this.UdpPorts, other.UdpPorts) &&
			ObjUtils.Equals(this.KeepOpenPortsOnly, other.KeepOpenPortsOnly));
	}

	public toString(): string {
		return this.Name;
	}

	public ToString(): string {
		return this.toString();
	}

	private IncludeCategoriesToString(): string {

		let includeCats = "";

		if (this.HasIncludeCategories) {

			this.IncludeCategories.ForEach((category: string) => {
				if (includeCats.length > 0)
					includeCats += ",";
				includeCats += category;
			});
		}

		return includeCats;
	}

	private ExcludeCategoriesToString(): string	{

		let excludeCats = "";

		if (this.HasExcludeCategories) {

			this.ExcludeCategories.ForEach((category: string) => {
				if (excludeCats.length > 0)
					excludeCats += ",";
				excludeCats += category;
			});
		}

		return excludeCats;
	}

	private IncludeCategoriesFromString(includeCats: string): void {

		this.IncludeCategories.Clear();

		if (!includeCats)
			return;

		let categories: string[] = includeCats.split(",");
		if (categories && (categories.length > 0)) {
			categories.forEach((value: string) => {
				if (value)
					this.IncludeCategories.Add(value);
			}, this);
		}
	}

	private ExcludeCategoriesFromString(excludeCats: string): void {

		this.ExcludeCategories.Clear();

		if (!excludeCats)
			return;

		let categories: string[] = excludeCats.split(",");
		if (categories && (categories.length > 0)) {
			categories.forEach((value: string) => {
				if (value)
					this.ExcludeCategories.Add(value);
			}, this);
		}
	}

	public Write(obj: any, context: any): void {
		obj.discovery_settings = {};
		obj = obj.discovery_settings;

		super.Write(obj, context);

		if (this.HasName)
			obj.name = this.Name;

		if (this.HasDescription)
			obj.description = this.Description;

		obj.discovery_method = DiscoveryMethod[this.Method];
		obj.max_threads = ValueEncoder.EncodeInteger(this.MaxThreads);
		obj.ping_first = ValueEncoder.EncodeBoolean(this.PingFirst);
		obj.timeout = ValueEncoder.EncodeInteger(this.PingTimeout);
		obj.retries = ValueEncoder.EncodeInteger(this.PingRetries);
		obj.resolve_host_names = ValueEncoder.EncodeBoolean(this.ResolveHostnames);
		obj.resolve_netbios = ValueEncoder.EncodeBoolean(this.ResolveNetBIOS);
		obj.capture_configs = ValueEncoder.EncodeBoolean(this.CaptureConfigs);
		obj.capture_logs = ValueEncoder.EncodeBoolean(this.CaptureLogs);
		obj.enable_cli_discovery = ValueEncoder.EncodeBoolean(this.EnableCliDiscovery);
		obj.allow_ipv6_crawl = ValueEncoder.EncodeBoolean(this.AllowIPv6Crawl);

		context[IpScope.ObjSer_ObjectName] = "seed_scope";
		this.SeedScope.Write(obj, context);

		context[IpScope.ObjSer_ObjectName] = "include_scope";
		this.IncludeScope.Write(obj, context);

		if (this.HasIncludeCategories) {
			let includeCats = this.IncludeCategoriesToString();
			if (includeCats)
				obj.include_categories = includeCats;
		}

		context[IpScope.ObjSer_ObjectName] = "exclude_scope";
		this.ExcludeScope.Write(obj, context);

		if (this.HasExcludeCategories) {
			let excludeCats = this.ExcludeCategoriesToString();
			if (excludeCats)
				obj.exclude_categories = excludeCats;
		}

		this.ProtocolSettings.Write(obj, context);

		if (this.HasMaxTotalTime)
			obj.max_total_time = ValueEncoder.EncodeTimeSpan(this.MaxTotalTime);

		if (this.HasMaxDeviceTime)
			obj.max_device_time = ValueEncoder.EncodeTimeSpan(this.MaxDeviceTime);

		if (this.HasSnmpReadDelay)
			obj.snmp_read_delay = ValueEncoder.EncodeInteger(this.SnmpReadDelay);

		if (this.HasSnmpReadDelay)
			obj.snmp_read_delay = ValueEncoder.EncodeInteger(this.SnmpReadDelay);

		obj.windows_full_inventory = ValueEncoder.EncodeBoolean(this.WinFullInventory);

		if (this.HasTcpPorts)
			obj.tcp_ports = this.TcpPorts;

		if (this.HasUdpPorts)
			obj.udp_ports = this.UdpPorts;

		if (this.KeepOpenPortsOnly)
			obj.keep_open_ports_only = ValueEncoder.EncodeBoolean(this.KeepOpenPortsOnly);
	}

	public Read(obj: any, context: any): void {
		this.Clear();

		if (!obj.discovery_settings)
			throw new Error();
		obj = obj.discovery_settings;

		super.Read(obj, context);

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.description !== undefined)
			this.Description = obj.description;

		if (obj.discovery_method !== undefined)
			this.Method = DiscoveryMethod[<string>obj.discovery_method];

		if (obj.max_threads !== undefined)
			this.MaxThreads = ValueEncoder.DecodeInteger(obj.max_threads);

		if (obj.ping_first !== undefined)
			this.PingFirst = ValueEncoder.DecodeBoolean(obj.ping_first);

		if (obj.timeout !== undefined)
			this.PingTimeout = ValueEncoder.DecodeInteger(obj.timeout);

		if (obj.retries !== undefined)
			this.PingRetries = ValueEncoder.DecodeInteger(obj.retries);

		if (obj.resolve_host_names !== undefined)
			this.ResolveHostnames = ValueEncoder.DecodeBoolean(obj.resolve_host_names);

		if (obj.resolve_netbios !== undefined)
			this.ResolveNetBIOS = ValueEncoder.DecodeBoolean(obj.resolve_netbios);

		if (obj.capture_configs !== undefined)
			this.CaptureConfigs = ValueEncoder.DecodeBoolean(obj.capture_configs);

		if (obj.capture_logs !== undefined)
			this.CaptureLogs = ValueEncoder.DecodeBoolean(obj.capture_logs);

		if (obj.enable_cli_discovery !== undefined)
			this.EnableCliDiscovery = ValueEncoder.DecodeBoolean(obj.enable_cli_discovery);

		if (obj.allow_ipv6_crawl !== undefined)
			this.AllowIPv6Crawl = ValueEncoder.DecodeBoolean(obj.allow_ipv6_crawl);

		if (obj.seed_scope !== undefined) {
			context[IpScope.ObjSer_ObjectName] = "seed_scope";
			this.SeedScope.Read(obj, context);
		}

		if (obj.include_scope !== undefined) {
			context[IpScope.ObjSer_ObjectName] = "include_scope";
			this.IncludeScope.Read(obj, context);
		}

		if (obj.include_categories !== undefined)
			this.IncludeCategoriesFromString(obj.include_categories);

		if (obj.exclude_scope !== undefined) {
			context[IpScope.ObjSer_ObjectName] = "exclude_scope";
			this.ExcludeScope.Read(obj, context);
		}

		if (obj.exclude_categories !== undefined)
			this.ExcludeCategoriesFromString(obj.exclude_categories);

		if (obj.protocol_settings_set !== undefined)
			this.ProtocolSettings.Read(obj, context);

		if (obj.max_total_time !== undefined)
			this.MaxTotalTime = ValueEncoder.DecodeTimeSpan(obj.max_total_time);

		if (obj.max_device_time !== undefined)
			this.MaxDeviceTime = ValueEncoder.DecodeTimeSpan(obj.max_device_time);

		if (obj.snmp_read_delay !== undefined)
			this.SnmpReadDelay = ValueEncoder.DecodeInteger(obj.snmp_read_delay);

		if (obj.windows_full_inventory !== undefined)
			this.WinFullInventory = ValueEncoder.DecodeBoolean(obj.windows_full_inventory);

		if (obj.tcp_ports !== undefined)
			this.TcpPorts = obj.tcp_ports;

		if (obj.udp_ports !== undefined)
			this.UdpPorts = obj.udp_ports;

		if (obj.keep_open_ports_only !== undefined)
			this.KeepOpenPortsOnly = ValueEncoder.DecodeBoolean(obj.keep_open_ports_only);
	}

}

